import { PrismicDocument } from '@prismicio/client';
import { prismicService } from '@services/businessContainer';
import {
  SlicesContainer,
  IPrismicCustomTypeContent,
  getPageContentsWithProducts,
} from '@screens/SlicesContainer';
import BreadcrumbList from '@components/BreadcrumbList';
import { NextSeo } from 'next-seo';
import getConfig from 'next/config';
import CustomError from '@screens/CustomError';
import HeartLoader from '@monofront/design-system/components/HeartLoader';
import { useAuthentication } from '@context/businessContainer';
import useRedirections, { IPrismicRules } from '@hooks/useRedirections';
import LayoutNoCTA from '@components/LayoutNoCTA';
import Layout from '@components/Layout';
import { useTranslations } from 'next-intl';
import { getChannelFromLocale } from '@monofront/business/utils';
import LayoutLightWithCart from '@components/LayoutLightWithCart';
import { useFeatureIsOn } from '@monofront/business/contexts';
import { getQueryParam } from '@utils/query';
import { GetStaticProps } from 'next';
import { LFPage, injectLayoutData } from '@utils/layout';

const {
  publicRuntimeConfig: { appBaseUrl },
} = getConfig();

interface IRegisterFunnelProps {
  redirectionRules: IPrismicRules | null;
  pageContents: IPrismicCustomTypeContent[];
  abTestContents?: IPrismicCustomTypeContent[];
  featureFlagKey: string;
  pageAuthContext: string;
  mobileHeaderTitle: string;
  previousPageLabel: string;
  previousPageSlug: string;
  layout: 'light' | 'full';
  saveStepUrl?: string;
  displayGiftBanner?: boolean;
}

const RegisterFunnel: LFPage<IRegisterFunnelProps> = ({
  redirectionRules,
  pageContents,
  abTestContents,
  featureFlagKey,
}) => {
  const isMaap = getQueryParam('isMaap') === 'true';

  useRedirections(!isMaap ? redirectionRules : null);
  const { isLoading } = useAuthentication();

  const isABTestEnabled = useFeatureIsOn(featureFlagKey);

  const tGlobal = useTranslations('Global');

  if (pageContents.length === 0) {
    return <CustomError statusCode={404} />;
  }

  if (isLoading) {
    return (
      <div className="loader-container">
        <HeartLoader />
      </div>
    );
  }

  return (
    <>
      <BreadcrumbList
        name={`${tGlobal('companyName')} | ${tGlobal('defaultBreadcrumb')}`}
      />
      <NextSeo
        canonical={appBaseUrl}
        openGraph={{
          url: appBaseUrl,
        }}
      />
      <SlicesContainer
        pageContents={
          isABTestEnabled && abTestContents ? abTestContents : pageContents
        }
      />

      <style jsx global>
        {`
          #__next {
            background-color: var(--clr-primary-5);
          }

          @media (max-width: 800px) {
            #__next {
              background: var(--white);
            }
          }
        `}
      </style>
    </>
  );
};

RegisterFunnel.getLayout = (props) => {
  switch (props.layout) {
    case 'Light':
      return LayoutNoCTA;
    case 'LightWithCart':
      return LayoutLightWithCart;
    default:
      return Layout;
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps = injectLayoutData<
  IRegisterFunnelProps,
  GetStaticProps<IRegisterFunnelProps>
>(async ({ previewData, params, locale }) => {
  if (!params?.slug) {
    return {
      notFound: true,
    };
  }

  const document: PrismicDocument = await prismicService.getDocumentByUid(
    'funnel',
    params.slug as string,
    previewData,
    {
      fetchLinks: ['redirection_rules.body', 'giftitembanner.body'],
      ...(locale ? { lang: `${locale}-${locale}` } : {}),
    },
  );

  if (!document) {
    return {
      notFound: true,
    };
  }
  const pageContents: IPrismicCustomTypeContent[] = document.data.body;
  const abTestContents: IPrismicCustomTypeContent[] =
    document.data.ab_test_body;
  const abTestContentsWithProducts: IPrismicCustomTypeContent[] = [];

  if (document.data.ab_test_enabled) {
    abTestContentsWithProducts.push(
      ...(await getPageContentsWithProducts(
        abTestContents,
        getChannelFromLocale(locale),
      )),
    );
  }

  const giftItemBanner =
    document.data.gift_item_banner?.data?.body.length > 0
      ? document.data.gift_item_banner?.data?.body[0].items
      : [];

  return {
    props: {
      redirectionRules: document.data.redirection_rules?.data?.body || null,
      pageContents,
      giftItemBanner,
      abTestContents: abTestContentsWithProducts,
      featureFlagKey: document.data.feature_flag_key || '',
      pageAuthContext: document.data.page_auth_context,
      mobileHeaderTitle: document.data.mobile_header_title,
      previousPageLabel: document.data.previous_page_label,
      previousPageSlug: document.data.previous_page_slug,
      layout: document.data.layout,
      saveStepUrl: document.data.save_step_url,
      displayGiftBanner: true,
    },
    revalidate: 1800, // 30min
  };
});

export default RegisterFunnel;
